import React, {
  ChangeEvent,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import ContactUsForm from './ContactUsForm';
import CurrentUserContext from '../../contexts/CurrentUserContext';
import AnalyticsContext from '../../contexts/AnalyticsContext';
import { submitContactUsForm } from '../../api/submitContactUsForm';
import VersionContext from '../../contexts/VersionContext';
import AccountPickerContext from '../../contexts/AccountPickerContext';
import AccountContext from '../../contexts/AccountContext';
import USER_ROLES from '../../roles';
import captureException from '../../services/captureException';
import BreadcrumbsContext from '../../contexts/BreadcrumbsContext';
import UnsavedChangesProvider, {
  UnsavedChangesContext,
} from '../../contextProviders/UnsavedChangesProvider';

const ContactUsFormContainer = ({ close }: { close: () => void }) => {
  const currentUser = useContext(CurrentUserContext);
  const { trackEvent, recordInHotjar } = useContext(AnalyticsContext);
  const { breadcrumbs } = useContext(BreadcrumbsContext);

  const { buildNumber } = useContext(VersionContext);
  const { id, email, displayName } = useContext(CurrentUserContext);
  const { selectedAccount } = useContext(AccountPickerContext);
  const { carrierName, id: accountId } = useContext(AccountContext);
  const { onCloseAttempt, setHasUnsavedChanges } = useContext(
    UnsavedChangesContext,
  );

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSuccess, setIsSuccess] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [subject, setSubject] = useState<string>('');
  const [type, setType] = useState<string>('');
  const [message, setMessage] = useState<string>('');
  const [attachments, setAttachments] = useState<File[]>([]);

  const collectMetadata = useCallback(() => {
    let role = '';
    if (selectedAccount.roles.includes(USER_ROLES.ADMIN)) {
      role = 'ADMIN';
    } else if (selectedAccount.roles.includes(USER_ROLES.VIEWER)) {
      role = 'VIEWER';
    } else if (selectedAccount.roles.includes(USER_ROLES.EDITOR)) {
      role = 'EDITOR';
    }
    return JSON.stringify({
      user: { displayName, role, email, id },
      carrier: { name: carrierName, id: accountId },
      url: window.location.href,
      userAgent: window.navigator.userAgent,
      buildNumber,
      screen: `${window.screen.availHeight}x${window.screen.availWidth}`,
      viewport: `${window.screen.height}x${window.screen.width}`,
      breadcrumbs: breadcrumbs.map((crumb) => ({
        name: crumb.name,
        id: crumb.id || undefined,
        url: crumb.url,
      })),
    });
  }, [
    buildNumber,
    displayName,
    email,
    id,
    carrierName,
    accountId,
    selectedAccount,
    breadcrumbs,
  ]);

  const onSubmit = useCallback(() => {
    if (currentUser === undefined) {
      alert('Input is not valid');
      return;
    }
    setIsLoading(true);
    setError('');
    const metadata = collectMetadata();
    submitContactUsForm({
      type,
      subject,
      message,
      attachments,
      metadata,
    })
      .then((success) => {
        trackEvent('Help - Contact Form Submitted');
        recordInHotjar('submitted_support_ticket', {
          type,
          subject,
          message,
        });
        if (success) {
          setIsSuccess(true);
          setHasUnsavedChanges(false);
        } else {
          const e = new Error();
          e.name = 'ContactUsForm: Failed to submit';
          captureException(e);
          setError(
            'Something went wrong. Please try again or email us directly at support@fleetops.com',
          );
        }
      })
      .catch((err) => {
        captureException(err);
        setError(
          'Something went wrong. Please try again or email us directly at support@fleetops.com',
        );
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [
    currentUser,
    setIsLoading,
    setIsSuccess,
    setHasUnsavedChanges,
    type,
    subject,
    message,
    attachments,
    collectMetadata,
    trackEvent,
    recordInHotjar,
  ]);

  const onSubjectChanged = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setSubject(event.target.value);
    },
    [setSubject],
  );

  const onMessageChanged = useCallback(
    (event: ChangeEvent<HTMLTextAreaElement>) => {
      setMessage(event.target.value);
    },
    [setMessage],
  );

  useEffect(() => {
    setHasUnsavedChanges(
      !!message || !!subject || !!type || attachments.length > 0,
    );
  }, [attachments.length, message, setHasUnsavedChanges, subject, type]);

  return (
    <ContactUsForm
      onSubmit={onSubmit}
      close={onCloseAttempt}
      isLoading={isLoading}
      isSuccess={isSuccess}
      error={error}
      subject={subject}
      onSubjectChanged={onSubjectChanged}
      type={type}
      setType={setType}
      message={message}
      onMessageChanged={onMessageChanged}
      attachments={attachments}
      setAttachments={setAttachments}
    />
  );
};

const Gate = ({ close }: { close: () => void }) => {
  return (
    <UnsavedChangesProvider close={close}>
      <ContactUsFormContainer close={close} />
    </UnsavedChangesProvider>
  );
};

export default Gate;
